import React, { ReactElement, ReactNode } from 'react';
import { TranslatedString, TranslationFunction } from '../../components/blocks';
import {
    DATE_OUTSIDE_WINDOW_TYPE,
    DUPLICATE_LINE_ITEM_TYPE,
    DUPLICATE_PURCHASE_ORDER_TYPE,
    FIELD_UNCONFIRMED_TYPE,
    LINE_ITEMS_MISSING_TYPE,
    LINE_ITEMS_TOTAL_MISMATCH_TYPE,
    LINE_ITEMS_UNCONFIRMED_TYPE,
    LINE_MAY_BE_SWAPPED_TYPE,
    LINE_POSITION_UNVERIFIED_TYPE,
    LOCATION_UNMATCHED_TYPE,
    ROW_UNCONFIRMED_TYPE,
} from '../../constants';
import { isOrderableQuantity } from '../../constants/orderQuantity';
import { PoReviewRow, ResellerItem } from '../../redux/types';
import { ExtractedIdentifierCode, ExtractedLineItemCandidate, ExtractionFinding } from '../../types';
import { identifierTypeOf } from '../../utils/resellerItemUtils';

// The page keys off the finding type, not the raw code. Many codes share one message, and a type
// this build does not know still shows instead of vanishing.

export const PO_NUMBER_FIELD = 'poNumber';

export const SHIP_DATE_FIELD = 'shipDate';

export const DELIVERY_DATE_FIELD = 'deliveryDate';

export const QUANTITY_FIELD = 'quantity';

// Item matches that leave the item empty until the reviewer picks one, so their marker clears once
// the field is valid.
const DIFFERENT_ITEMS_MATCH = 'DIFFERENT_ITEMS';
const NO_ITEM_MATCH = 'NO_ITEM';
const NO_CODES_MATCH = 'NO_CODES';

const FORCED_ITEM_MATCHES = [DIFFERENT_ITEMS_MATCH, NO_ITEM_MATCH, NO_CODES_MATCH];

const SUGGESTED_ITEMS_MATCH = 'SUGGESTED_ITEMS';

// HRPS pre-fills the item when the suggestions amount to one item, which the reviewer confirms. With
// several it leaves the item empty, so the row behaves like a forced match.
function isForcedMatch(row: PoReviewRow): boolean {
    if (row.itemMatch === SUGGESTED_ITEMS_MATCH) {
        return candidateItemIds(row).length !== 1;
    }
    return row.itemMatch !== undefined && FORCED_ITEM_MATCHES.includes(row.itemMatch);
}

export type LineItemsNotice =
    | typeof LINE_ITEMS_TOTAL_MISMATCH_TYPE
    | typeof LINE_ITEMS_MISSING_TYPE
    | typeof LINE_ITEMS_UNCONFIRMED_TYPE
    | null;

export function lineItemsNotice(tableFindings: ExtractionFinding[]): LineItemsNotice {
    const hasType = (type: string) => tableFindings.some((finding) => finding.type === type);
    if (hasType(LINE_ITEMS_TOTAL_MISMATCH_TYPE)) {
        return LINE_ITEMS_TOTAL_MISMATCH_TYPE;
    }
    if (hasType(LINE_ITEMS_MISSING_TYPE)) {
        return LINE_ITEMS_MISSING_TYPE;
    }
    const hasGatedNotice = tableFindings.some(
        (finding) => finding.type !== LINE_ITEMS_TOTAL_MISMATCH_TYPE && finding.type !== LINE_ITEMS_MISSING_TYPE
    );
    if (hasGatedNotice) {
        return LINE_ITEMS_UNCONFIRMED_TYPE;
    }
    return null;
}

const LINE_ITEMS_MISSING_FINDING: ExtractionFinding = { type: LINE_ITEMS_MISSING_TYPE };

export function submitConfirmationNotice(notice: LineItemsNotice): LineItemsNotice {
    return notice === LINE_ITEMS_MISSING_TYPE ? null : notice;
}

export function lineItemsNoticeMessage(t: TranslationFunction, notice: LineItemsNotice): TranslatedString | undefined {
    switch (notice) {
        case LINE_ITEMS_TOTAL_MISMATCH_TYPE:
            return t('lineItemsTotalMismatch-notice');
        case LINE_ITEMS_MISSING_TYPE:
            return findingMessage(t, LINE_ITEMS_MISSING_FINDING);
        case LINE_ITEMS_UNCONFIRMED_TYPE:
            return t('lineItemsUnconfirmed-notice');
        default:
            return undefined;
    }
}

// The scanned-document notice is derived from the document metadata, not a row, and shows alongside any
// line-items notice.
export function scannedDocumentNotice(t: TranslationFunction): TranslatedString {
    return t('scannedDocument-notice');
}

const LABELLED_FIELDS = [QUANTITY_FIELD, PO_NUMBER_FIELD, SHIP_DATE_FIELD, DELIVERY_DATE_FIELD];

const HEADER_FIELDS = [PO_NUMBER_FIELD, SHIP_DATE_FIELD, DELIVERY_DATE_FIELD];

const KNOWN_MESSAGE_TYPES = [
    FIELD_UNCONFIRMED_TYPE,
    ROW_UNCONFIRMED_TYPE,
    LINE_MAY_BE_SWAPPED_TYPE,
    LINE_POSITION_UNVERIFIED_TYPE,
    DUPLICATE_LINE_ITEM_TYPE,
    LINE_ITEMS_MISSING_TYPE,
    LOCATION_UNMATCHED_TYPE,
    DUPLICATE_PURCHASE_ORDER_TYPE,
    DATE_OUTSIDE_WINDOW_TYPE,
];

function findingIdentity(finding: ExtractionFinding): string {
    return `${finding.type}.${finding.field ?? 'whole'}`;
}

export function findingKey(finding: ExtractionFinding, keyPrefix: string): string {
    return `${keyPrefix}.${findingIdentity(finding)}`;
}

// One entry per distinct concern. The service can send the same finding more than once.
export function distinctFindings(findings: ExtractionFinding[]): ExtractionFinding[] {
    const seen = new Set<string>();
    const distinct: ExtractionFinding[] = [];
    findings.forEach((finding) => {
        const identity = findingIdentity(finding);
        if (!seen.has(identity)) {
            seen.add(identity);
            distinct.push(finding);
        }
    });
    return distinct;
}

// The sentence is translated with this sentinel in place of {{codes}}, then split around it so the codes
// can be rendered as React nodes.
const CODES_PLACEHOLDER = '\u0000';

function renderCode(code: ExtractedIdentifierCode, index: number): ReactNode {
    const label = code.identifierType ? `${code.identifierType}: ${code.value}` : code.value;
    return (
        <React.Fragment key={index}>
            {index > 0 && ', '}
            <strong>{label}</strong>
        </React.Fragment>
    );
}

export function showsResolutionSentence(row: PoReviewRow): boolean {
    if (row.itemMatch === undefined) {
        return false;
    }
    return !(isForcedMatch(row) && Boolean(row.itemId));
}

// Reads nothing once the concern has cleared; a match the page does not recognize reads the generic
// wording so a new match never renders nowhere.
export function resolutionSentence(
    t: TranslationFunction,
    row: PoReviewRow,
    itemMap: Map<string, ResellerItem>
): TranslatedString | ReactElement | undefined {
    if (!showsResolutionSentence(row)) {
        return undefined;
    }
    const codes = row.codes ?? [];
    const sentence = t('itemMatch', {
        context: sentenceContext(row),
        count: codes.length,
        codes: CODES_PLACEHOLDER,
        ...suggestedItemsValues(row, itemMap),
    });
    const [before, after] = sentence.split(CODES_PLACEHOLDER);
    if (after === undefined) {
        return sentence;
    }
    return (
        <span>
            {before}
            {codes.map(renderCode)}
            {after}
        </span>
    );
}

function sentenceContext(row: PoReviewRow): string | undefined {
    if (row.itemMatch !== SUGGESTED_ITEMS_MATCH) {
        return row.itemMatch;
    }
    return candidateItemIds(row).length === 1 ? `${SUGGESTED_ITEMS_MATCH}_ONE` : `${SUGGESTED_ITEMS_MATCH}_MANY`;
}

function suggestedItemsValues(row: PoReviewRow, itemMap: Map<string, ResellerItem>): { items?: string } {
    if (row.itemMatch !== SUGGESTED_ITEMS_MATCH) {
        return {};
    }
    const labels = distinctCandidates(row).map((candidate) => candidateLabel(candidate, itemMap.get(candidate.itemId)));
    return { items: labels.join(', ') };
}

function candidateLabel(candidate: ExtractedLineItemCandidate, item: ResellerItem | undefined): string {
    const type = identifierTypeOf(candidate.identifier, item);
    const identifier = type ? `${type} ${candidate.identifier}` : candidate.identifier;
    return item?.title ? `${identifier} (${item.title})` : identifier;
}

function distinctCandidates(row: PoReviewRow): ExtractedLineItemCandidate[] {
    const byItemId = new Map<string, ExtractedLineItemCandidate>();
    (row.codes ?? [])
        .flatMap((code) => code.candidates ?? [])
        .forEach((candidate) => byItemId.has(candidate.itemId) || byItemId.set(candidate.itemId, candidate));
    return [...byItemId.values()];
}

export function candidateItemIds(row: PoReviewRow): string[] {
    return distinctCandidates(row).map((candidate) => candidate.itemId);
}

// ROW_UNCONFIRMED covers a price the page never shows, so a stronger row signal takes precedence. It
// shows only when it is the row's only signal.
export function rowFindingsToRender(row: PoReviewRow): ExtractionFinding[] {
    const distinct = withoutUnusableQuantityFinding(distinctFindings(row.findings), row);
    const hasStrongerSignal =
        showsResolutionSentence(row) ||
        hasLiveForcedQuantityConcern(row) ||
        distinct.some((finding) => finding.type !== ROW_UNCONFIRMED_TYPE);
    return hasStrongerSignal ? distinct.filter((finding) => finding.type !== ROW_UNCONFIRMED_TYPE) : distinct;
}

function withoutUnusableQuantityFinding(findings: ExtractionFinding[], row: PoReviewRow): ExtractionFinding[] {
    return row.quantityNotRead
        ? findings.filter((finding) => !(finding.type === FIELD_UNCONFIRMED_TYPE && finding.field === QUANTITY_FIELD))
        : findings;
}

// The quantity was read but is unusable and the box is still empty or invalid, so its sentence and
// marker still apply. Typing a valid quantity clears this forced concern.
export function hasLiveForcedQuantityConcern(row: PoReviewRow): boolean {
    return row.quantityNotRead === true && !isOrderableQuantity(row.quantity);
}

// A forced match leaves the submit confirmation once the reviewer picks an item; only an unforced
// match keeps the row in it.
function hasUnforcedResolutionConcern(row: PoReviewRow): boolean {
    return row.itemMatch !== undefined && !isForcedMatch(row);
}

// A live unforced concern points at a value we could not verify. It never clears, so it sets the row
// to Review, lists in the submit confirmation, and keeps its marker and sentence until submit.
export function hasLiveUnforcedConcern(row: PoReviewRow): boolean {
    return rowFindingsToRender(row).length > 0 || hasUnforcedResolutionConcern(row);
}

export function hasRowDetail(row: PoReviewRow): boolean {
    return rowFindingsToRender(row).length > 0 || showsResolutionSentence(row) || hasLiveForcedQuantityConcern(row);
}

export const ITEM_CELL = 'item';
export const QUANTITY_CELL = 'quantity';

export type UnconfirmedCell = typeof ITEM_CELL | typeof QUANTITY_CELL;

// The amber cell marker shows while the concern is live. A forced concern drops its marker once the
// field is valid; an unforced concern keeps it.
export function unconfirmedCells(row: PoReviewRow): Set<UnconfirmedCell> {
    const cells = new Set<UnconfirmedCell>();
    if (marksItemCell(row)) {
        cells.add(ITEM_CELL);
    }
    if (marksQuantityCell(row)) {
        cells.add(QUANTITY_CELL);
    }
    return cells;
}

const ITEM_CELL_TYPES = [LINE_POSITION_UNVERIFIED_TYPE, DUPLICATE_LINE_ITEM_TYPE];

function marksItemCell(row: PoReviewRow): boolean {
    if (row.findings.some((finding) => ITEM_CELL_TYPES.includes(finding.type))) {
        return true;
    }
    if (row.itemMatch === undefined) {
        return false;
    }
    return isForcedMatch(row) ? !row.itemId : true;
}

function marksQuantityCell(row: PoReviewRow): boolean {
    return hasLiveForcedQuantityConcern(row) || hasUnconfirmedQuantityFinding(rowFindingsToRender(row));
}

function hasUnconfirmedQuantityFinding(findings: ExtractionFinding[]): boolean {
    return findings.some((finding) => finding.type === FIELD_UNCONFIRMED_TYPE && finding.field === QUANTITY_FIELD);
}

function findingFieldLabel(t: TranslationFunction, field?: string): TranslatedString {
    return field && LABELLED_FIELDS.includes(field) ? t('findingField', { context: field }) : t('findingField');
}

// Extra values fill in text that cannot be translated, such as the printed ship-to address.
export function findingMessage(
    t: TranslationFunction,
    finding: ExtractionFinding,
    values: Record<string, string> = {}
): TranslatedString {
    return t('findingMessage', {
        context: messageType(finding),
        field: findingFieldLabel(t, finding.field),
        ...values,
    });
}

function messageType(finding: ExtractionFinding): string {
    if (KNOWN_MESSAGE_TYPES.includes(finding.type)) {
        return finding.type;
    }
    return finding.field && HEADER_FIELDS.includes(finding.field) ? FIELD_UNCONFIRMED_TYPE : ROW_UNCONFIRMED_TYPE;
}

export function isPoNumberFinding(finding: ExtractionFinding): boolean {
    return finding.field === PO_NUMBER_FIELD;
}

export function isShipDateFinding(finding: ExtractionFinding): boolean {
    return finding.field === SHIP_DATE_FIELD;
}

export function isDeliveryDateFinding(finding: ExtractionFinding): boolean {
    return finding.field === DELIVERY_DATE_FIELD;
}

export function isDateOutsideWindowFinding(finding: ExtractionFinding): boolean {
    return finding.type === DATE_OUTSIDE_WINDOW_TYPE;
}

export function addressFindingsToRender(findings: ExtractionFinding[]): ExtractionFinding[] {
    return distinctFindings(findings).filter((finding) => finding.type === LOCATION_UNMATCHED_TYPE);
}
